/**
 * THE LIST VIEW, in the pieces every list screen shares.
 *
 * A list on a phone is a stack of cards you scroll with a thumb. The same list on a desk is a
 * table you scan with your eyes, sort by a column, and work from a panel on the right. Those are
 * two shapes of one thing, so this file draws both from ONE array of rows: `DataTable` renders the
 * cards below the md breakpoint and the table above it, and neither is a separate list that can
 * drift out of step with the other.
 *
 * Everything a finger lands on is at least 44px tall, at both widths, including a column header:
 * sorting a table is a thing somebody does on a tablet with a thumb, and a 20px header is not a
 * target. That is why the header row here is taller than a spreadsheet's on a tablet. At a desk,
 * the `desk:` variant in styles.css, a pointer lands on it instead, and the header, the rows and
 * every control in the bar take a desk's 36px (ops #56).
 *
 * A TABLE NEVER CLIPS AND NEVER PUSHES THE PAGE. It stands in a frame that scrolls sideways when
 * its columns need more room than it has, with the scroll shown (`scroll-frame` in styles.css), and
 * the table is never narrower than its columns can be read at (`tableMinWidth`), so the column
 * with no width of its own cannot be squeezed to nothing (ops #56).
 *
 * The panel is a panel on the right once the page is wide enough to hold it beside a readable
 * list, from xl, and an inline block under the list below that, never a modal. A modal on a phone
 * hides the row it is about, and this interface is read standing up.
 */
import { Fragment, isValidElement, useEffect, useRef, useState, type ReactNode } from 'react';
import { TARGET_SIZE } from './Field.tsx';

/**
 * The small uppercase caption over a group of rows. One definition, used by the cards and by the
 * caption row inside the table, so a group is named the same way at both widths.
 */
const CAPTION = 'text-xs leading-4 tracking-wide text-navy/55 uppercase';

/**
 * The caption over a group of cards. Inside a table, `DataTable` draws it as a spanning row.
 *
 * It carries no top margin, because a caption is always the FIRST child of the block it names, so
 * `first:mt-0` would win over anything put here and the gap would never appear. The space above a
 * group belongs to the group's own wrapper, which is the thing with siblings.
 */
export function GroupCaption({ children }: { children: ReactNode }) {
  return <p className={`mb-2 ${CAPTION}`}>{children}</p>;
}

/**
 * True from the md breakpoint up, the same 48rem Tailwind uses, so a screen can decide not to ASK
 * for what it is not going to draw. `DataTable` needs no such thing, since both its shapes are
 * rendered from rows already in hand; this is for the panels beside a list, whose contents cost a
 * request each and would otherwise be fetched over mobile data to be hidden by a media query.
 *
 * It reads the viewport, which is not a clock. Nothing in this interface reads a wall clock.
 */
export function useWideView(): boolean {
  return useViewport(WIDE);
}

/**
 * True from the lg breakpoint up, 64rem, for the one table with more columns than md has room
 * for. `screens/Sources.tsx` carries eleven, and eleven shares of the 470px the rail and the page
 * padding leave at md are 42px each: every header word overflows its own cell and the numbers sit
 * under the wrong ones. So the report shows five columns on a tablet and all eleven on a desk,
 * which is a decision about what is worth reading at each width rather than a table that fits
 * nowhere. Below md there is no table at all and the cards carry every number.
 *
 * This answers about the WINDOW, and a panel open beside the table takes 400px of it, so the
 * screen asks a second question of its own: `everyColumnFits` in `web/src/reports.ts` puts the
 * table back to five columns while a channel is open, and the panel that took the room shows that
 * channel's ten numbers itself.
 */
export function useWiderView(): boolean {
  return useViewport(WIDER);
}

/**
 * True from the xl breakpoint up, 80rem, which is where `SplitLayout` stands its panel BESIDE the
 * list rather than under it (ops #56). A screen asks it to decide what its list carries while a
 * panel is open: beside, the panel takes 420px of the row, so the list merges its less needed
 * columns into the first one; under, the list keeps the page's width and every column it has.
 */
export function useBesideView(): boolean {
  return useViewport(BESIDE);
}

function useViewport(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const listen = () => setMatches(media.matches);
    listen();
    media.addEventListener('change', listen);
    return () => media.removeEventListener('change', listen);
  }, [query]);
  return matches;
}

const WIDE = '(min-width: 48rem)';
const WIDER = '(min-width: 64rem)';
/** The same 80rem as Tailwind's xl, the breakpoint `SplitLayout` puts its panel beside at. */
export const BESIDE = '(min-width: 80rem)';

/* ------------------------------------------------------------------ *
 * The filter bar.
 * ------------------------------------------------------------------ */

/**
 * The row of controls over a list. Stacked on a phone, a single row on a desk, with `action`
 * pushed to the far right: that is where the one thing you came to the screen to START belongs,
 * away from the six things that only narrow what you are looking at.
 *
 * THE END OF THE BAR WRAPS AS ONE (ops #56). `tail` is a control that travels with the action,
 * such as a tick that changes what is asked for, and the two stand in one group that grows to the
 * end of the row. Where the filters and the group fit on one row, the tail follows the filters and
 * the action keeps the far right; where they do not, the whole group takes the second row, the
 * tail at its left and the action at its right, so no control is ever left alone on a line of its
 * own while the buttons crowd the corner, which is what the owner's screenshot of 2026-09-28
 * showed.
 */
export function FilterBar({
  children,
  action,
  tail,
}: {
  children: ReactNode;
  action?: ReactNode;
  tail?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-col gap-3 rounded-xl border border-navy/12 bg-parchment p-3 md:flex-row md:flex-wrap md:items-center md:gap-x-4 md:gap-y-2 md:px-4 md:py-2">
      {children}
      {action || tail ? (
        <div
          data-bar-end=""
          className="flex flex-col gap-3 md:grow md:flex-row md:flex-wrap md:items-center md:gap-x-4 md:gap-y-2"
        >
          {tail}
          {action ? <div className="md:ml-auto">{action}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One select in the bar. The empty string is the "anything" answer and is always first, because a
 * filter that cannot be taken off is a trap.
 */
export function FilterSelect<T extends string>({
  id,
  label,
  value,
  onChange,
  options,
  toLabel,
  anyLabel,
  className = '',
}: {
  id: string;
  label: string;
  value: T | '';
  onChange: (value: T | '') => void;
  options: readonly T[];
  toLabel: (value: T) => string;
  /** The word for no filter at all. Omit it and the field has to hold one of the options. */
  anyLabel?: string;
  className?: string;
}) {
  return (
    <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-2.5">
      <label htmlFor={id} className={CAPTION}>
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value as T | '')}
        className={`w-full rounded-lg border border-navy/25 bg-parchment px-3 text-navy md:w-40 ${TARGET_SIZE} ${className}`}
      >
        {anyLabel ? <option value="">{anyLabel}</option> : null}
        {options.map((option) => (
          <option key={option} value={option}>
            {toLabel(option)}
          </option>
        ))}
      </select>
    </div>
  );
}

/** A tick in the bar. 44px of label, 36px at a desk, so the target is the sentence and not the box. */
export function FilterToggle({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label htmlFor={id} className={`flex items-center gap-3 text-sm font-medium ${TARGET_SIZE}`}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-5 w-5 shrink-0 accent-navy"
      />
      <span>{label}</span>
    </label>
  );
}

/* ------------------------------------------------------------------ *
 * The table, and the cards it is the same list as.
 * ------------------------------------------------------------------ */

/** What `aria-sort` says, in the words the attribute itself uses. */
export type SortDirection = 'ascending' | 'descending' | 'none';

/**
 * A column header you can press. The chevrons are one 12px mark with two halves, and the half
 * that matches the current order is the darker one, so the direction is legible without colour.
 */
export function SortableHeader({
  label,
  direction,
  onSort,
}: {
  label: string;
  direction: SortDirection;
  onSort: () => void;
}) {
  const up = direction === 'ascending' ? 'stroke-navy/70' : 'stroke-navy/30';
  const down = direction === 'descending' ? 'stroke-navy/70' : 'stroke-navy/30';
  return (
    <button
      type="button"
      onClick={onSort}
      className={`flex w-full items-center gap-1 text-left ${TARGET_SIZE} ${CAPTION}`}
    >
      {/* The caption's size rides on the span as well: styles.css sets every control's type
          outside a cascade layer, so on the button alone the 12px caption drew at 16px. */}
      <span className={CAPTION}>{label}</span>
      <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        className="h-3 w-3 shrink-0"
        fill="none"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 8l4-4 4 4" className={up} />
        <path d="M6 12l4 4 4-4" className={down} />
      </svg>
    </button>
  );
}

export interface DataColumn<Row> {
  readonly key: string;
  /** A word, or a `SortableHeader`. */
  readonly header: ReactNode;
  readonly cell: (row: Row) => ReactNode;
  /** A CSS width lifted from the artboard, such as '168px'. The column with none takes the rest. */
  readonly width?: string;
  /**
   * The least a column with no pixel width may be squeezed to before the table scrolls in its
   * frame, in pixels such as '104px'. Omitted, the column that takes the rest keeps
   * `REST_FLOOR_PX` and a share keeps `SHARE_FLOOR_PX`. See `tableMinWidth`.
   */
  readonly minWidth?: string;
  /** Extra classes on the body cell. The default is 14px navy. */
  readonly cellClassName?: string;
  /** Set when this column decides the order, so a reader is told which one does. */
  readonly sort?: SortDirection;
}

/** Rows under a caption. One group with no caption is an ungrouped list. */
export interface RowGroup<Row> {
  readonly key: string;
  readonly caption?: string;
  readonly rows: readonly Row[];
}

const HEAD_CELL =
  'h-11 desk:h-9 px-2 text-left align-middle first:pl-4 last:pr-4 border-b border-navy/12 font-semibold';
/** Row padding: 12px above and below under a thumb, 8px at a desk. */
export const BODY_CELL =
  'px-2 py-3 desk:py-2 align-middle text-sm wrap-break-word first:pl-4 last:pr-4';
/** The first cell sets a row's least height: 56px under a thumb, 44px at a desk. */
export const FIRST_CELL = 'h-14 desk:h-11';

/**
 * The least width the column that takes the rest is read at. It is usually the widest words on the
 * row, a title or a next action, so it keeps room for a short line of them.
 */
export const REST_FLOOR_PX = 160;

/** The least width a column that takes a share of the table is read at: a pill, a figure, a word. */
export const SHARE_FLOOR_PX = 80;

/** A width such as '168px' as a number of pixels, or null for a share, nothing, or anything else. */
function pixelsOf(width: string | undefined): number | null {
  if (width === undefined || !width.endsWith('px')) return null;
  const pixels = Number(width.slice(0, -2));
  return Number.isFinite(pixels) && pixels > 0 ? pixels : null;
}

/** A width such as '26%' as a fraction of the table, or null for pixels, nothing, or anything else. */
function shareOf(width: string | undefined): number | null {
  if (width === undefined || !width.endsWith('%')) return null;
  const percent = Number(width.slice(0, -1));
  return Number.isFinite(percent) && percent > 0 && percent < 100 ? percent / 100 : null;
}

/**
 * THE NARROWEST A TABLE IS DRAWN (ops #56). A fixed table takes the greater of its own width and
 * the sum of its columns, and a column with no width of its own gets whatever is left, which beside
 * the rail at 768 was nothing: the words of the one column that pays for every other stood on top
 * of each other. So the table is never narrower than every pixel column plus a floor for each of
 * the others, its own `minWidth` or else the rest's or a share's, and when the frame is narrower
 * than that, the frame scrolls.
 *
 * A SHARE IS A SHARE OF THE TABLE, so two floors are worked out as the table lays itself out and
 * not as a sum. The columns with no width split what the pixels and the shares leave, so the
 * table is at least wide enough that the shares' part of it leaves them their floors. And a share
 * that names its own `minWidth`, such as a column of pills that do not wrap, keeps it only once
 * the table is that width divided by the share: 168px at 26% is a table of 646px.
 */
export function tableMinWidth(
  columns: readonly { readonly width?: string; readonly minWidth?: string }[],
): string {
  let total = 0;
  let pixels = 0;
  let rest = 0;
  let shares = 0;
  let byShare = 0;
  for (const column of columns) {
    const px = pixelsOf(column.width);
    const share = shareOf(column.width);
    const own = pixelsOf(column.minWidth);
    const floor = column.width === undefined ? REST_FLOOR_PX : SHARE_FLOOR_PX;
    total += px ?? own ?? floor;
    if (px !== null) pixels += px;
    else if (share !== null) {
      shares += share;
      if (own !== null) byShare = Math.max(byShare, own / share);
    } else rest += own ?? floor;
  }
  const forTheRest = shares < 1 ? (pixels + rest) / (1 - shares) : 0;
  return `${Math.ceil(Math.max(total, forTheRest, byShare))}px`;
}

/**
 * One list, two shapes. `card` draws a row below md, the columns draw it above, and both are
 * handed the same object, so a column nobody put on a card is a decision and not an oversight.
 */
export function DataTable<Row>({
  groups,
  columns,
  rowKey,
  card,
  rowClassName,
  headerless,
  label,
}: {
  groups: readonly RowGroup<Row>[];
  columns: readonly DataColumn<Row>[];
  rowKey: (row: Row) => string;
  card: (row: Row) => ReactNode;
  rowClassName?: (row: Row) => string;
  /** Drops the header row, for the second of two tables that share one set of column words. */
  headerless?: boolean;
  /** What the table is called, since its heading usually sits outside it on the screen. */
  label?: string;
}) {
  const flat = groups.flatMap((group) => group.rows);
  const last = flat.length > 0 ? rowKey(flat[flat.length - 1] as Row) : null;

  return (
    <>
      <div className="md:hidden">
        {groups.map((group) => (
          <div key={group.key} className="mt-4 first:mt-0">
            {group.caption ? <GroupCaption>{group.caption}</GroupCaption> : null}
            <ul className="space-y-3">
              {group.rows.map((row) => (
                <li key={rowKey(row)}>{card(row)}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="scroll-frame hidden rounded-xl border border-navy/12 bg-parchment shadow-[0_1px_2px_rgba(22,41,63,0.06)] md:block">
        <table
          className="w-full table-fixed border-collapse"
          style={{ minWidth: tableMinWidth(columns) }}
          aria-label={label}
        >
          <colgroup>
            {columns.map((column) => (
              <col key={column.key} style={column.width ? { width: column.width } : undefined} />
            ))}
          </colgroup>
          {headerless ? null : (
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    aria-sort={column.sort}
                    className={`${HEAD_CELL} ${CAPTION}`}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {groups.map((group) => (
              <Fragment key={group.key}>
                {group.caption ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className={`border-y border-navy/8 bg-navy/4 px-4 py-1.5 ${CAPTION}`}
                    >
                      {group.caption}
                    </td>
                  </tr>
                ) : null}
                {group.rows.map((row) => {
                  const key = rowKey(row);
                  return (
                    <tr
                      key={key}
                      className={`${key === last ? '' : 'border-b border-navy/8'} ${
                        rowClassName ? rowClassName(row) : ''
                      }`}
                    >
                      {columns.map((column, index) => (
                        <td
                          key={column.key}
                          className={`${BODY_CELL} ${index === 0 ? FIRST_CELL : ''} ${
                            column.cellClassName ?? ''
                          }`}
                        >
                          {column.cell(row)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * The split, and the panel on the right of it.
 * ------------------------------------------------------------------ */

/**
 * The list, and beside it the row you are working on. Below xl the panel falls under the list
 * instead of over it: a sheet that covers the row it is about is a worse answer on a phone.
 *
 * BESIDE ONLY FROM XL (ops #56). The panel is 400px, and at md the rail and the page padding leave
 * 472px, so beside it the list was about fifty pixels wide; at lg it was about three hundred. From
 * xl the list keeps at least 560px beside the panel, and below that it keeps the page's width.
 */
export function SplitLayout({ children, panel }: { children: ReactNode; panel?: ReactNode }) {
  const place = useRef<HTMLDivElement>(null);
  const opened = panelIdentity(panel);
  /*
   * UNDER THE LIST, THE PANEL IS BROUGHT TO THE ROW THAT OPENED IT (ops #56). Below xl the panel
   * stands after the whole list, so pressing a row on a tablet or a small laptop drew it a screen
   * or more below the pointer and the press read as doing nothing. Each time a panel opens, or
   * opens on another row, it is scrolled just far enough to be seen; beside the list it is
   * already in view and nothing moves.
   */
  useEffect(() => {
    if (opened === null) return;
    const element = place.current;
    if (element === null || typeof element.scrollIntoView !== 'function') return;
    if (window.matchMedia(BESIDE).matches) return;
    element.scrollIntoView({ block: 'nearest' });
  }, [opened]);
  if (!panel) return <div className="min-w-0">{children}</div>;
  return (
    <div className="xl:grid xl:grid-cols-[minmax(0,1fr)_400px] xl:items-start xl:gap-5">
      <div className="min-w-0">{children}</div>
      <div ref={place} className="mt-4 min-w-0 scroll-mt-24 xl:mt-0">
        {panel}
      </div>
    </div>
  );
}

/**
 * Which panel is open, for the scroll above: its key where the screen keys it by the row, which
 * every screen does so that another row draws its own boxes, or simply open. Null when none is.
 */
export function panelIdentity(panel: ReactNode): string | null {
  if (panel === null || panel === undefined || panel === false) return null;
  return isValidElement(panel) && panel.key !== null ? `row:${String(panel.key)}` : 'open';
}

/** The chrome of the panel. What goes inside it is the screen's business, not this file's. */
export function SidePanel({
  title,
  pills,
  onClose,
  children,
}: {
  title: ReactNode;
  pills?: ReactNode;
  onClose?: () => void;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2.5 rounded-xl border border-navy/12 bg-parchment p-4 shadow-[0_1px_2px_rgba(22,41,63,0.06)]">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="min-w-0 grow font-serif text-xl leading-tight">{title}</h2>
        {pills}
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-navy/25 text-sm font-medium desk:min-h-9 desk:min-w-9"
            aria-label="Close this panel"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 20 20"
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5.5 5.5l9 9M14.5 5.5l-9 9" />
            </svg>
          </button>
        ) : null}
      </div>
      {children}
    </section>
  );
}

/** The hairline the panel uses between one job and the next. */
export function PanelRule() {
  return <div className="h-px bg-navy/10" />;
}
